import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/src/components/ui/card';
import type { Client } from '@repo/shared-types';
import { Tags } from '../../../components/Tags';
import { useUpdateClient } from '../../../hooks/useClients';
import { useCan } from '../../../hooks/useCan';
import { toast } from 'sonner';

interface InfoTabProps {
  client: Client;
}

interface FormState {
  name: string;
  email: string;
  zaloPhone: string;
}

function deriveForm(client: Client): FormState {
  return {
    name: client.name,
    email: client.email ?? '',
    zaloPhone: client.zaloPhone ?? '',
  };
}

export function InfoTab({ client }: InfoTabProps) {
  const { t } = useTranslation('clients');
  const { mutate: updateClient, isPending } = useUpdateClient(client.id);
  const canManage = useCan('manage:clients');

  const [form, setForm] = useState<FormState>(() => deriveForm(client));

  // No useEffect needed to re-sync form: the parent ClientDetailPage remounts
  // this component (via React Router navigation) when the client id changes,
  // and after a save mutation, the user's edits already equal the new client
  // values (so isDirty naturally becomes false).

  const isDirty =
    form.name !== client.name ||
    form.email !== (client.email ?? '') ||
    form.zaloPhone !== (client.zaloPhone ?? '');

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    updateClient(
      {
        name: form.name.trim(),
        email: form.email.trim() || null,
        zaloPhone: form.zaloPhone.trim() || null,
      },
      { onSuccess: () => toast.success(t('info.save')) },
    );
  };

  const handleAddTag = (tag: string) => {
    if (client.tags.includes(tag)) return;
    updateClient({ tags: [...client.tags, tag] });
  };

  const handleRemoveTag = (tag: string) => {
    updateClient({ tags: client.tags.filter((existing) => existing !== tag) });
  };

  return (
    <div className="space-y-6">
      {/* Tags */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('tags.label')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Tags
            tags={client.tags}
            mode={canManage ? 'edit' : 'read'}
            onAdd={handleAddTag}
            onRemove={handleRemoveTag}
          />
        </CardContent>
      </Card>

      {/* Info form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('info.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="info-name">{t('info.name')}</Label>
              <Input
                id="info-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                disabled={!canManage}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="info-email">{t('info.email')}</Label>
              <Input
                id="info-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={!canManage}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="info-phone">{t('info.zaloPhone')}</Label>
              <Input
                id="info-phone"
                value={form.zaloPhone}
                onChange={(e) => setForm({ ...form, zaloPhone: e.target.value })}
                disabled={!canManage}
              />
            </div>
            {canManage && (
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setForm(deriveForm(client))}
                  disabled={!isDirty}
                >
                  {t('info.cancel')}
                </Button>
                <Button type="submit" disabled={!isDirty || isPending || !form.name.trim()}>
                  {isPending ? t('info.saving') : t('info.save')}
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
